/**
 * Classify an error from `user.getMe`.
 *
 * Defaults to `api-unreachable`: only a completed round-trip that came back
 * UNAUTHORIZED (or HTTP 401) is treated as proof the session is invalid. An
 * unrecognized error shape means we could not read an answer, which is exactly
 * the case where the session must be preserved.
 *
 * @param error - Anything thrown by the query — a `TRPCClientError`, a raw
 *   `TypeError` from `fetch`, `null`, or an unrelated value.
 * @returns Which of the two failure classes the error belongs to.
 *
 * @example
 * classifyAuthError(unauthorizedError); // 'session-invalid'
 * classifyAuthError(new TypeError('Failed to fetch')); // 'api-unreachable'
 */
export const classifyAuthError = (error) => {
    if (!error || typeof error !== 'object') {
        return 'api-unreachable';
    }
    const { data, meta } = error;
    if (data && typeof data === 'object') {
        if (data.code === 'UNAUTHORIZED' || data.httpStatus === 401) {
            return 'session-invalid';
        }
    }
    // A 401 raised outside tRPC's error envelope (an Auth.js redirect, a gateway
    // rejecting the cookie) carries no `data`, so fall back to the raw response.
    if (meta?.response?.status === 401) {
        return 'session-invalid';
    }
    return 'api-unreachable';
};
/**
 * True when the error proves the session is no longer valid.
 *
 * @param error - The error from `user.getMe`.
 */
export const isSessionInvalidError = (error) => classifyAuthError(error) === 'session-invalid';
/**
 * True when the error means the API could not be reached or could not answer.
 *
 * @param error - The error from `user.getMe`.
 */
export const isApiUnreachableError = (error) => classifyAuthError(error) === 'api-unreachable';
